import React, { useMemo, useState, useCallback, RefObject, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { List } from 'tea-component';
import { useIntl, useSelector, shallowEqual } from 'umi';
import classNames from 'classnames';
import { useClickAway, useHover } from 'ahooks';
import SvgIcon from '@home/components/SvgIcon';
import CommonBubble from '@home/components/CommonBubble';
import { getSmartTableActionsList, getSmartTableExportTypes } from '../../constants';
import { SmartTableActions } from '../../../ChatDSTemplate/type';
import { useSmartTableActions } from '../../hooks';
import type { ISmartTableGridRef } from '../SmartTableGrid';
import type { TSheetDataMap } from '../../hooks/useSheetData';
import type { ISheet } from '../ExcelSheetTabs';
import AIPolishPopover from '../AIPolishPopover';
import aiPolishIcon from '@/pages/aigc/assets/svg/agent-ai-polish.svg';
import undoIcon from '@/pages/aigc/assets/svg/agent-ai-table-back.svg';
import undoDisabledIcon from '@/pages/aigc/assets/svg/agent-ai-table-back-disabled.svg';
import redoIcon from '@/pages/aigc/assets/svg/agent-ai-table-redo.svg';
import redoDisabledIcon from '@/pages/aigc/assets/svg/agent-ai-table-redo-disabled.svg';
import fullScreenIcon from '@/pages/aigc/assets/svg/agent-full-screen.svg';
import exitFullScreenIcon from '@/pages/aigc/assets/svg/agent-exit-fullscreen.svg';
import './index.scss';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { useSessionId } from '../../../ChatPage/hooks/useAgentModalSata';
import { concat } from 'lodash-es';
import { HIDDEN_ORGANIZATIONS } from '@/pages/aigc/components/OrganizationPermission';

/** 暴露给父组件的方法（用于 SmartTableGrid 刷新撤销/重做状态） */
export interface ITableActionsBarRef {
  refreshUndoRedoState: () => void;
}

interface ITableActionsBarProps {
  /** 表格组件 ref */
  gridRef?: RefObject<ISmartTableGridRef>;
  /** 获取表格数据回调（可选，优先使用 gridRef） */
  getTableData?: () => string;
  /** 所有 sheet 数据 Map（保存时打印用） */
  sheetDataMap?: TSheetDataMap;
  /** 所有 sheet 信息列表 */
  sheets?: ISheet[];
  /** 当前 sheet 名称（用于导出文件名） */
  sheetName?: string;
  /** 是否禁用所有操作按钮（加载中时禁用） */
  disabled?: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 重置回调（用于获取原始文件数据） */
  onReset?: () => Promise<void>;
  /** 自定义类名 */
  className?: string;
  /** 当前文件名（用于保存时上传） */
  currentFileName?: string;
  /** 当前文件的 COS 路径（后端返回，用于保存时上传到正确路径） */
  currentFileCosUrl?: string;
  /** 是否为只读模式（分享场景下为 true，隐藏重置/保存/关闭按钮） */
  readonly?: boolean;
  /** 保存成功后的回调（用于刷新 Excel 数据） */
  onSaveSuccess?: () => Promise<void> | void;
  /** 是否全屏状态 */
  isFullScreen?: boolean;
  /** 切换全屏回调 */
  onToggleFullScreen?: () => void;
  /** AI 润色弹层是否可见（受控） */
  aiPolishVisible?: boolean;
  /** AI 润色按钮点击回调 */
  onAIPolishClick?: () => void;
  /** AI 润色关闭回调 */
  onAIPolishClose?: () => void;
  /** AI 润色发送回调 */
  onAIPolish?: (prompt: string) => void;
  /** 表格名称（用于 AI 润色弹层显示） */
  tableName?: string;
}

/**
 * 表格操作按钮组组件
 * 包含撤销/重做、AI润色按钮、操作按钮列表、导出下拉菜单、分隔线和关闭按钮
 * 布局顺序：撤销 重做 | AI润色表格 重置 | 复制 导出 保存 | 全屏 关闭
 */
const TableActionsBar = forwardRef<ITableActionsBarRef, ITableActionsBarProps>(({
  gridRef,
  getTableData,
  sheetDataMap,
  sheets,
  sheetName,
  disabled = false,
  onClose,
  onReset,
  className,
  currentFileName,
  currentFileCosUrl,
  readonly = false,
  onSaveSuccess,
  isFullScreen = false,
  onToggleFullScreen,
  aiPolishVisible = false,
  onAIPolishClick,
  onAIPolishClose,
  onAIPolish,
  tableName: aiPolishTableName,
}, ref) => {
  // 内部获取国际化语言
  const { formatMessage } = useIntl();
  const userInfo = useSelector(
    (state: any) => state.home.userInfo,
    shallowEqual,
  );
  const isTechlandOrg = useMemo(
    () => (userInfo?.organization || []).some(
      (org: string) => HIDDEN_ORGANIZATIONS.includes(org),
    ),
    [userInfo?.organization],
  );
  const { chatList, bookMarkedChats } = useChatList({ manual: true });
  const { sessionId } = useSessionId();
  const sensitiveConversation = useMemo(
    () => concat(chatList, bookMarkedChats).find((chat) => chat.session_id === sessionId)?.has_sensitive_data,
    [chatList, bookMarkedChats, sessionId],
  );
  // 操作事件 Hook（状态下沉到组件内部）
  const { handleAction, showCopyTips, handleExport, isSaving } = useSmartTableActions({
    gridRef,
    getTableData,
    sheetName,
    onReset,
    currentFileName,
    currentFileCosUrl,
    sheetDataMap,
    sheets,
    onSaveSuccess,
  });

  // ==================== 撤销/重做状态 ====================
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const refreshUndoRedoState = useCallback(() => {
    if (gridRef?.current) {
      setCanUndo(gridRef.current.isUndoAvailable());
      setCanRedo(gridRef.current.isRedoAvailable());
    }
  }, [gridRef]);

  useImperativeHandle(ref, () => ({
    refreshUndoRedoState,
  }), [refreshUndoRedoState]);

  const handleUndo = useCallback(() => {
    gridRef?.current?.undo();
    refreshUndoRedoState();
  }, [gridRef, refreshUndoRedoState]);

  const handleRedo = useCallback(() => {
    gridRef?.current?.redo();
    refreshUndoRedoState();
  }, [gridRef, refreshUndoRedoState]);

  // ==================== AI 润色（状态由父组件控制） ====================

  // ==================== 导出下拉菜单状态 ====================
  // 控制下拉菜单是否可见
  const [exportDropdownVisible, setExportDropdownVisible] = useState(false);
  // 是否通过点击打开的（点击打开后不会因为 hover 离开而关闭）
  const isClickOpenedRef = useRef(false);
  // 延迟关闭的定时器
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 下拉菜单容器 ref
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  // 触发按钮 ref
  const exportButtonRef = useRef<HTMLDivElement>(null);
  
  // 监听 hover 状态
  const isDropdownHovering = useHover(exportDropdownRef);
  const isButtonHovering = useHover(exportButtonRef);
  
  // 点击外部关闭下拉菜单
  useClickAway(
    () => {
      if (exportDropdownVisible) {
        setExportDropdownVisible(false);
        isClickOpenedRef.current = false;
      }
    },
    [exportDropdownRef, exportButtonRef],
  );
  
  // 监听 hover 状态，自动关闭下拉菜单
  useEffect(() => {
    // 如果下拉菜单未打开，不处理
    if (!exportDropdownVisible) {
      return;
    }
    
    // 清除之前的定时器
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    
    // 如果鼠标在按钮或下拉菜单内，保持打开
    if (isDropdownHovering || isButtonHovering) {
      return;
    }
    
    // 如果是通过点击打开的，不自动关闭
    if (isClickOpenedRef.current) {
      return;
    }
    
    // 延迟关闭，给用户时间移动鼠标
    closeTimerRef.current = setTimeout(() => {
      setExportDropdownVisible(false);
    }, 200);
    
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, [isDropdownHovering, isButtonHovering, exportDropdownVisible]);
  
  // 处理导出按钮 hover
  const handleExportButtonHover = useCallback(() => {
    if (!isClickOpenedRef.current) {
      setExportDropdownVisible(true);
    }
  }, []);
  
  // 处理导出按钮点击
  const handleExportButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // 点击时始终打开并标记为点击打开状态（点击已展开时不关闭）
    setExportDropdownVisible(true);
    isClickOpenedRef.current = true;
  }, []);
  
  // 处理导出选项点击
  const handleExportItemClick = useCallback((exportType: string) => {
    handleExport(exportType);
    setExportDropdownVisible(false);
    isClickOpenedRef.current = false;
  }, [handleExport]);

  // 敏感权限：禁止导出（Techland 组织统一放开）
  const isExportDisabled = !!sensitiveConversation && !isTechlandOrg;

  // 综合禁用状态：加载中 或 保存中
  const isDisabled = disabled || isSaving;

  // 操作按钮列表（只读模式下过滤掉重置和保存按钮）
  const actionsList = useMemo(() => {
    const allActions = getSmartTableActionsList(formatMessage, showCopyTips);
    if (readonly) {
      return allActions.filter(
        (item) => item.key !== SmartTableActions.Reset && item.key !== SmartTableActions.Save
      );
    }
    return allActions;
  }, [formatMessage, showCopyTips, readonly]);

  // 导出类型列表
  const exportTypes = getSmartTableExportTypes(formatMessage);

  /** 通用操作按钮点击处理（抽取重复逻辑） */
  const handleItemClick = useCallback((item: any, isItemDisabled: boolean) => {
    if (isItemDisabled) return;
    if (item.key === SmartTableActions.FullScreen) {
      onToggleFullScreen?.();
      return;
    }
    handleAction(item);
  }, [handleAction, onToggleFullScreen]);

  return (
    <div className={classNames('dba-table-actions-bar', className, { 'dba-table-actions-bar--disabled': isDisabled })}>
      {/* ===== 撤销/重做按钮 ===== */}
      {!readonly && (
        <>
          <CommonBubble content={`${formatMessage({ id: 'dba_smart_table_undo' })} (Ctrl+Z)`}>
            <button
              className={classNames('dba-table-actions-bar__icon-btn', { disabled: !canUndo })}
              onClick={handleUndo}
              disabled={!canUndo}
            >
              <img src={canUndo ? undoIcon : undoDisabledIcon} alt="" className="dba-table-actions-bar__btn-icon" />
            </button>
          </CommonBubble>
          <CommonBubble content={`${formatMessage({ id: 'dba_smart_table_redo' })} (Ctrl+Y)`}>
            <button
              className={classNames('dba-table-actions-bar__icon-btn', { disabled: !canRedo })}
              onClick={handleRedo}
              disabled={!canRedo}
            >
              <img src={canRedo ? redoIcon : redoDisabledIcon} alt="" className="dba-table-actions-bar__btn-icon" />
            </button>
          </CommonBubble>

          {/* 分隔线：重做按钮右侧 */}
          <div className="dba-table-actions-bar__divider" />
        </>
      )}

      {/* ===== AI 润色按钮 ===== */}
      {!readonly && (
        <div className="dba-table-actions-bar__ai-wrapper">
          {!aiPolishVisible ? (
            <CommonBubble content={formatMessage({ id: 'dba_smart_table_ai_polish' })}>
              <button
                className={classNames('dba-table-actions-bar__ai-btn', { active: aiPolishVisible })}
                onClick={onAIPolishClick}
              >
                <img src={aiPolishIcon} alt="" className="dba-table-actions-bar__ai-icon" />
                <span className="dba-table-actions-bar__ai-text">{formatMessage({ id: 'dba_smart_table_ai_polish' })}</span>
              </button>
            </CommonBubble>
          ) : (
            <button
              className={classNames('dba-table-actions-bar__ai-btn', { active: aiPolishVisible })}
              onClick={onAIPolishClick}
            >
              <img src={aiPolishIcon} alt="" className="dba-table-actions-bar__ai-icon" />
              <span className="dba-table-actions-bar__ai-text">{formatMessage({ id: 'dba_smart_table_ai_polish' })}</span>
            </button>
          )}

          {/* AI 润色弹层 - 渲染在按钮容器内，通过 position: absolute 定位在按钮正下方 */}
          <AIPolishPopover
            visible={aiPolishVisible}
            tableName={aiPolishTableName}
            onClose={onAIPolishClose || (() => {})}
            onSend={onAIPolish || (() => {})}
          />
        </div>
      )}

      {/* ===== 操作按钮列表 ===== */}
      {actionsList.map((item, index) => {
        // 导出按钮在敏感权限下禁用
        const isItemDisabled = isDisabled || (item.key === SmartTableActions.Export && isExportDisabled);

        // 重置按钮右侧加分隔线
        const showDividerAfterReset = item.key === SmartTableActions.Reset;
        // 保存按钮右侧加分隔线
        const showDividerAfterSave = item.key === SmartTableActions.Save;

        const isWithText = item.key === SmartTableActions.Reset;

        // 获取 hover 提示文字
        const getHoverText = () => {
          switch (item.key) {
            case SmartTableActions.Copy:
              // 复制成功时隐藏 hover 提示，等复制成功状态消失后再显示
              return showCopyTips ? '' : formatMessage({ id: 'dba_smart_table_copy' });
            case SmartTableActions.Export:
              if (isExportDisabled) {
                return formatMessage({ id: 'dba_smart_table_export_sensitive_disabled' });
              }
              return formatMessage({ id: 'dba_smart_table_export' });
            case SmartTableActions.Save:
              return formatMessage({ id: 'dba_smart_table_save' });
            case SmartTableActions.FullScreen:
              return isFullScreen
                ? formatMessage({ id: 'dba_smart_table_exit_fullscreen' })
                : formatMessage({ id: 'dba_smart_table_fullscreen' });
            default:
              return '';
          }
        };

        const hoverText = getHoverText();

        // 渲染按钮内容
        const renderButtonContent = () => {
          if (item.key === SmartTableActions.FullScreen) {
            return (
              <div className="dba-chat-ds-report-page-right-resent dba-smart-table-action-icon">
                <img src={isFullScreen ? exitFullScreenIcon : fullScreenIcon} alt="" />
              </div>
            );
          }
          return item.label;
        };

        // 根据按钮类型渲染不同包装器，避免嵌套三元
        const renderActionWrapper = () => {
          if (item.key === SmartTableActions.Export) {
            const exportNode = (
              <div
                ref={exportButtonRef}
                className={classNames('dba-table-actions-bar__item', 'dba-table-actions-bar__export-wrapper', {
                  'dba-table-actions-bar__item--disabled': isItemDisabled,
                })}
                onMouseEnter={isItemDisabled ? undefined : handleExportButtonHover}
                onClick={isItemDisabled ? undefined : handleExportButtonClick}
              >
                {item.label}
                {/* 导出下拉菜单 */}
                {exportDropdownVisible && !isItemDisabled && (
                  <div
                    ref={exportDropdownRef}
                    className="dba-table-actions-bar__export-dropdown"
                  >
                    <List type="option" style={{ maxWidth: '300px' }}>
                      {exportTypes.map((exportItem) => (
                        <List.Item
                          key={exportItem.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportItemClick(exportItem.value);
                          }}
                        >
                          <img src={exportItem.icon} alt="" className="dba-table-actions-bar__export-dropdown-icon" />
                          {exportItem.label}
                        </List.Item>
                      ))}
                    </List>
                  </div>
                )}
              </div>
            );

            if (hoverText) {
              return (
                <CommonBubble
                  content={hoverText}
                  arrowPointAtCenter={isExportDisabled}
                  overlayClassName={isExportDisabled ? 'dba-table-actions-bar__export-sensitive-tooltip' : undefined}
                >
                  {exportNode}
                </CommonBubble>
              );
            }

            return exportNode;
          }

          const buttonNode = (
            <div
              className={classNames('dba-table-actions-bar__item', {
                'dba-table-actions-bar__item--disabled': isItemDisabled,
                'dba-table-actions-bar__item--with-text': isWithText,
              })}
              onClick={() => handleItemClick(item, isItemDisabled)}
            >
              {renderButtonContent()}
            </div>
          );

          if (hoverText) {
            return <CommonBubble content={hoverText}>{buttonNode}</CommonBubble>;
          }

          return buttonNode;
        };

        return (
          <React.Fragment key={index}>
            {renderActionWrapper()}
            {showDividerAfterReset && <div className="dba-table-actions-bar__divider" />}
            {showDividerAfterSave && <div className="dba-table-actions-bar__divider" />}
          </React.Fragment>
        );
      })}

      {/* ===== 关闭按钮 ===== */}
      <SvgIcon
        type="clonse"
        className="dba-table-actions-bar__close"
        onClick={onClose}
      />
    </div>
  );
});

TableActionsBar.displayName = 'TableActionsBar';

export default TableActionsBar;
